import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Form,
  TimePicker,
  Switch,
  message,
  Space,
  Typography,
  Row,
  Col,
  Spin,
  Tag,
  Alert,
  theme,
} from 'antd';
import {
  ClockCircleOutlined,
  SaveOutlined,
  ReloadOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { clinicHoursService } from '../../services/clinicHoursService';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const ClinicHoursManagement = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clinicHours, setClinicHours] = useState(null);
  
  const {
    token: { colorBgContainer, colorBgLayout, borderRadiusLG, colorTextSecondary, colorPrimary },
  } = theme.useToken();

  const daysOfWeek = [
    { key: 'monday', label: 'Montag', short: 'Mo' },
    { key: 'tuesday', label: 'Dienstag', short: 'Di' },
    { key: 'wednesday', label: 'Mittwoch', short: 'Mi' },
    { key: 'thursday', label: 'Donnerstag', short: 'Do' },
    { key: 'friday', label: 'Freitag', short: 'Fr' },
    { key: 'saturday', label: 'Samstag', short: 'Sa' },
    { key: 'sunday', label: 'Sonntag', short: 'So' },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicHoursService.getClinicHours();
      setClinicHours(data);
      
      // Set form values
      const formValues = {};
      daysOfWeek.forEach(day => {
        const dayData = data[day.key];
        formValues[`${day.key}_isOpen`] = dayData?.isOpen || false;
        
        if (dayData?.timeSlots && dayData.timeSlots.length > 0) {
          dayData.timeSlots.forEach((slot, index) => {
            formValues[`${day.key}_start_${index}`] = slot.start ? dayjs(slot.start, 'HH:mm') : null;
            formValues[`${day.key}_end_${index}`] = slot.end ? dayjs(slot.end, 'HH:mm') : null;
          });
        }
      });
      
      form.setFieldsValue(formValues);
    } catch (error) {
      console.error(error);
      message.error('Fehler beim Laden der Sprechzeiten');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const hoursData = {};
      
      daysOfWeek.forEach(day => {
        const isOpen = values[`${day.key}_isOpen`];
        const timeSlots = [];
        
        if (isOpen) {
          // Check for morning slot
          const morningStart = values[`${day.key}_start_0`];
          const morningEnd = values[`${day.key}_end_0`];
          if (morningStart && morningEnd) {
            timeSlots.push({
              start: morningStart.format('HH:mm'),
              end: morningEnd.format('HH:mm')
            });
          }
          
          // Check for afternoon slot
          const afternoonStart = values[`${day.key}_start_1`];
          const afternoonEnd = values[`${day.key}_end_1`];
          if (afternoonStart && afternoonEnd) {
            timeSlots.push({
              start: afternoonStart.format('HH:mm'),
              end: afternoonEnd.format('HH:mm')
            });
          }
        }
        
        hoursData[day.key] = {
          isOpen,
          timeSlots
        };
      });

      await clinicHoursService.updateClinicHours(hoursData);
      message.success('Sprechzeiten erfolgreich aktualisiert');
      fetchData(); // Refresh data
    } catch (error) {
      console.error(error);
      message.error('Fehler beim Aktualisieren der Sprechzeiten');
    } finally {
      setSubmitting(false);
    }
  };

  const renderDaySchedule = (day) => {
    return (
      <Card 
        key={day.key} 
        size="small" 
        style={{ 
          marginBottom: 16,
          backgroundColor: colorBgContainer,
          borderRadius: borderRadiusLG
        }}
        title={
          <Space>
            <Tag color="blue">{day.short}</Tag>
            <Text strong>{day.label}</Text>
          </Space>
        }
        extra={
          <Form.Item
            name={`${day.key}_isOpen`}
            valuePropName="checked"
            style={{ margin: 0 }}
            shouldUpdate={(prevValues, currentValues) => 
              prevValues[`${day.key}_isOpen`] !== currentValues[`${day.key}_isOpen`]
            }
          >
            <Switch checkedChildren="Geöffnet" unCheckedChildren="Geschlossen" />
          </Form.Item>
        }
      >
        <Form.Item shouldUpdate={(prevValues, currentValues) => 
          prevValues[`${day.key}_isOpen`] !== currentValues[`${day.key}_isOpen`]
        }>
          {({ getFieldValue }) => {
            const isOpen = getFieldValue(`${day.key}_isOpen`);
            
            if (isOpen) {
              return (
                <div>
                  <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col span={24}>
                      <Text strong style={{ color: colorPrimary }}>Vormittags</Text>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={12}>
                      <Form.Item
                        name={`${day.key}_start_0`}
                        label="Von"
                        style={{ margin: 0 }}
                      >
                        <TimePicker
                          format="HH:mm"
                          placeholder="Start"
                          style={{ width: '100%' }}
                          minuteStep={15}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`${day.key}_end_0`}
                        label="Bis"
                        style={{ margin: 0 }}
                      >
                        <TimePicker
                          format="HH:mm"
                          placeholder="Ende"
                          style={{ width: '100%' }}
                          minuteStep={15}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  
                  <Row gutter={16} style={{ marginBottom: 12 }}>
                    <Col span={24}>
                      <Text strong style={{ color: colorPrimary }}>Nachmittags</Text>
                      <Text type="secondary" style={{ marginLeft: 8 }}>(optional)</Text>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name={`${day.key}_start_1`}
                        label="Von"
                        style={{ margin: 0 }}
                      >
                        <TimePicker
                          format="HH:mm"
                          placeholder="Start"
                          style={{ width: '100%' }}
                          minuteStep={15}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name={`${day.key}_end_1`}
                        label="Bis"
                        style={{ margin: 0 }}
                      >
                        <TimePicker
                          format="HH:mm"
                          placeholder="Ende"
                          style={{ width: '100%' }}
                          minuteStep={15}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </div>
              );
            }
            
            return (
              <div style={{ 
                textAlign: 'center', 
                padding: '20px 0',
                backgroundColor: colorBgLayout,
                borderRadius: borderRadiusLG,
                margin: '8px 0'
              }}>
                <Text type="secondary">Geschlossen</Text>
              </div>
            );
          }}
        </Form.Item>
      </Card>
    );
  };

  const renderCurrentSchedule = () => {
    if (!clinicHours) return null;

    return (
      <Card 
        title={
          <Space>
            <CalendarOutlined />
            <span>Aktuelle Sprechzeiten</span>
          </Space>
        }
        style={{ 
          marginBottom: 24,
          backgroundColor: colorBgContainer 
        }}
      >
        <Row gutter={16}>
          {daysOfWeek.map(day => {
            const dayData = clinicHours[day.key];
            return (
              <Col span={8} key={day.key} style={{ marginBottom: 16 }}>
                <div style={{ 
                  padding: 12, 
                  backgroundColor: colorBgLayout, 
                  borderRadius: borderRadiusLG,
                  border: `1px solid ${colorBgLayout}`,
                  transition: 'all 0.3s ease'
                }}>
                  <Text strong>{day.label}</Text>
                  <br />
                  {dayData?.isOpen && dayData?.timeSlots?.length > 0 ? (
                    dayData.timeSlots.map((slot, index) => (
                      <div key={index}>
                        <Text style={{ fontSize: '12px' }}>
                          {slot.start} - {slot.end} Uhr
                        </Text>
                        <br />
                      </div>
                    ))
                  ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Geschlossen</Text>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </Card>
    );
  };

  return (
    <div style={{ 
      padding: '24px', 
      background: colorBgLayout, 
      minHeight: '100vh' 
    }}>
      <Card style={{ 
        marginBottom: 24,
        backgroundColor: colorBgContainer 
      }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Title level={2} style={{ margin: 0 }}>
              <ClockCircleOutlined style={{ marginRight: 8 }} />
              Sprechzeiten verwalten
            </Title>
            <Text type="secondary">Konfigurieren Sie die Öffnungszeiten Ihrer Praxis</Text>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchData}
                loading={loading}
              >
                Aktualisieren
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Alert
        message="Sprechzeiten Information"
        description="Sie können für jeden Tag bis zu zwei Zeiträume definieren (z.B. Vormittags und Nachmittags). Lassen Sie Felder leer, wenn nur ein Zeitraum benötigt wird."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {renderCurrentSchedule()}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Card 
            title="Sprechzeiten bearbeiten"
            style={{ backgroundColor: colorBgContainer }}
            extra={
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={submitting}
                size="large"
              >
                Speichern
              </Button>
            }
          >
            <Row gutter={24}>
              <Col span={8}>
                {daysOfWeek.slice(0, 3).map(renderDaySchedule)}
              </Col>
              <Col span={8}>
                {daysOfWeek.slice(3, 5).map(renderDaySchedule)}
              </Col>
              <Col span={8}>
                {daysOfWeek.slice(5, 7).map(renderDaySchedule)}
              </Col>
            </Row>
          </Card>
        </Form>
      </Spin>
    </div>
  );
};

export default ClinicHoursManagement;
